import { z } from "zod";

const PAGE_SIZE = 10;

// Keep members in sync with SupervisorAssignmentSortBy in ../../domain/models.
export const supervisorAssignmentSortBySchema = z.enum([
  "supervisorName",
  "assignedAssetsCount",
  "updatedAt",
]);

// One filter -- last updated date -- per the Supervisor Assignment list's
// user story (search already covers supervisor name / assigned-asset count).
export const supervisorAssignmentsSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    sortBy: supervisorAssignmentSortBySchema.optional(),
    // No `.trim()` here -- this value round-trips through the URL on every
    // keystroke (the search input is fully controlled by router state), so
    // trimming on parse would strip a trailing space the instant it's typed.
    // The actual request still trims (see toQuery in the list screen).
    search: z.string().min(1).optional(),
    updatedFrom: z.string().datetime().optional(),
    updatedTo: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.updatedFrom === undefined) === (search.updatedTo === undefined),
  );

export type SupervisorAssignmentsSearch = z.infer<
  typeof supervisorAssignmentsSearchSchema
>;

// Keep members in sync with SupervisorAssignmentHistorySortBy in
// ../../domain/models.
export const supervisorAssignmentHistorySortBySchema = z.enum([
  "supervisorName",
  "assetName",
  "assignedAt",
  "actionAt",
  "action",
]);

// Two filters -- assigned date and action date -- per the History
// endpoint's own query params (fromAssignedAt/toAssignedAt,
// fromActionAt/toActionAt); search covers supervisor/asset/assigned-by name
// and action server-side.
export const supervisorAssignmentHistorySearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    sortBy: supervisorAssignmentHistorySortBySchema.optional(),
    search: z.string().min(1).optional(),
    // Keep members in sync with SupervisorAssignmentHistoryAction in
    // ../../domain/models.
    action: z.enum(["Created", "Updated", "Removed"]).optional().catch(undefined),
    fromAssignedAt: z.string().datetime().optional(),
    toAssignedAt: z.string().datetime().optional(),
    fromActionAt: z.string().datetime().optional(),
    toActionAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromAssignedAt === undefined) === (search.toAssignedAt === undefined),
  )
  .refine(
    (search) =>
      (search.fromActionAt === undefined) === (search.toActionAt === undefined),
  );

export type SupervisorAssignmentHistorySearch = z.infer<
  typeof supervisorAssignmentHistorySearchSchema
>;
